/**
 * bina.az CDN photo URL helpers.
 *
 * The aggregator index stores the tiny `…/uploads/thumbnail/…` variant
 * of each photo (bina's smallest, ~lowest-quality size). bina's own
 * browse cards use a much sharper `…/uploads/f460x345/…` variant. We
 * hotlink the same CDN, so we can swap the size token at render time to
 * get a visibly crisper card image for free - no rehosting, no extra
 * fetch on the backend.
 *
 * Centralised here so every surface that renders a card thumbnail uses
 * the same transform (and so we have one place to bump the variant if
 * bina changes its CDN scheme).
 */

/**
 * Strip-sized variant for a photo-intel URL. Photo intel stores the
 * `uploads/full/` original; the result page paints it into a 144x112
 * thumbnail, and on phones that strip sits above the fold, so the full
 * file was the largest-contentful-paint element (/h mobile LCP 10 s
 * median in PostHog). Non-bina URLs pass through untouched; callers
 * fall back to the original on error.
 */
export function stripThumb(url: string): string {
  return url.replace(/\/uploads\/(full|thumbnail)\//, "/uploads/f460x345/");
}

/**
 * A card photo at the size its own site uses on its cards, sharp on any
 * screen. bina's `uploads/thumbnail/` becomes the `f460x345` variant, the
 * exact size bina serves on its own browse cards (bina's CDN only
 * generates specific size tokens; `f720x540` 404s). tap.az and lalafo.az
 * come in at full size, which made them most of a listing page's weight
 * (2026-10-19), so each takes its own card size. Anything else is
 * returned unchanged.
 */
export function upgradeCardImage(url: string | null | undefined): string | null {
  if (!url) return null;
  return url
    .replace("/uploads/thumbnail/", "/uploads/f460x345/")
    // tap.az runs bina's CDN scheme; the index keeps the full original
    // (~190 KB). Its 460x345 card size is about a third of that.
    .replace(/^(https:\/\/tap\.azstatic\.com\/uploads\/)full\//, "$1f460x345/")
    // lalafo.az: the original (~190 KB) becomes lalafo's own 370 px "api"
    // size (~30 KB), plenty for a card.
    .replace(/^(https:\/\/img\d*\.lalafo\.com\/i\/posters\/)original\//, "$1api/");
}

/**
 * The small copy a site keeps for its own grid, for a phone-width card:
 * ev10.az's 280 px square, emlaktap.az's 260 px "thumbnail", arenda.az's
 * 220x180 "homepage" copy, 10-25 KB against 80-130 KB. Side by side they
 * match the full file in a 182 px phone card and look soft in the 261 px
 * desktop card (2026-10-19), so they go into a `(max-width: 640px)`
 * source only. Null when the photo has no such copy. yeniemlak.az names
 * its small copies differently from the full photo, so it has none here.
 */
export function phoneCardImage(url: string | null | undefined): string | null {
  const card = upgradeCardImage(url);
  if (!card) return null;
  const small = card
    .replace(/^(https:\/\/cdn\.ev10\.az\/uploaded_images\/)high_quality\//, "$1medium_quality/")
    .replace(/^(https:\/\/static\.emlaktap\.az\/public\/uploads\/[^/]+\/)medium\//, "$1thumbnail/")
    // arenda keeps the small copy flat under the photo's hash.
    .replace(
      /^(https:\/\/arenda\.az\/uploads\/emlak\/thumbs\/)big_gallery\/[0-9a-f]{2}\/[0-9a-f]{2}\/([0-9a-f]{32}\.[a-z]+)$/,
      "$1homepage/$2",
    );
  return small === card ? null : small;
}

/* ── Card photos resized on our server ──────────────────────────────
 *
 * The API fetches a listing site's photo once, keeps a WebP per card
 * width on its own disk and Cloudflare keeps each copy at the edge
 * (backend services/card_photos, route /api/media/photo.webp). A card
 * then loads 8-20 KB instead of the site's 30-190 KB file (phones paint
 * the first card's photo as the page's largest paint). Only the sites
 * the API host can fetch: yeniemlak.az and emlak.az answer it 403, so
 * their cards keep the original URL, like any host not listed here.
 * The site's own card URL stays the fallback, and the route itself
 * answers 302 to the original on any failure. */

/** The widths the route makes (backend card_photos.WIDTHS). */
export const CARD_PHOTO_WIDTHS = [240, 360, 480, 640] as const;
/** Part of the URL and of every cache key (backend card_photos.VERSION). */
const CARD_PHOTO_VERSION = "1";

/** Photos on these hosts and paths, over https, without a query: the
 *  backend's allowlist (card_photos._SOURCES), nothing wider. */
const RESIZABLE =
  /^https:\/\/(?:bina\.azstatic\.com\/uploads\/|tap\.azstatic\.com\/uploads\/|img\d{0,2}\.lalafo\.com\/i\/posters\/|cdn\.ev10\.az\/uploaded_images\/|static\.emlaktap\.az\/public\/uploads\/|arenda\.az\/uploads\/)[^?#\s]+$/;

/** What `sizes` says per card layout: the browser picks the copy that
 *  covers the card at the screen's density. */
export const CARD_SIZES = {
  /** Two cards a row on phones (half the screen less the 16 px gutters
   *  and the 12 px gap: 173 px on a 390 px phone), three on tablets,
   *  about 260-300 px above. */
  grid: "(max-width: 639px) calc(50vw - 22px), (max-width: 1023px) 33vw, 300px",
  /** One card a row on phones (the station pages). */
  wide: "(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 360px",
  /** The row card's photo column (w-40, up to w-72). */
  row: "(max-width: 639px) 160px, 288px",
  /** Three small cards a row (a listing page's cheaper similar flats). */
  small: "(max-width: 639px) 33vw, 200px",
  /** A list thumbnail, 56-64 px. */
  thumb: "64px",
} as const;

/** The API's origin for media: `NEXT_PUBLIC_API_URL` is
 *  https://api.menzil.ai/api in prod (the Worker's /api rewrite fails on
 *  binary responses, so media go straight there, as in lib/api
 *  mediaSrc); locally it is relative and Next's dev rewrite proxies. */
function mediaOrigin(): string {
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base || base.startsWith("/")) return "";
  try {
    return new URL(base).origin;
  } catch {
    return "";
  }
}

/** The photo the API resizes: the site's card size where the stored URL
 *  is bina's tiny thumbnail or tap.az's full original, else as stored
 *  (lalafo's original, ev10's high_quality, emlaktap's medium, arenda's
 *  800 px gallery copy: fetched once, so the larger the sharper). */
function resizeSource(url: string): string {
  return url
    .replace("/uploads/thumbnail/", "/uploads/f460x345/")
    .replace(/^(https:\/\/tap\.azstatic\.com\/uploads\/)full\//, "$1f460x345/");
}

/** The API's `w` px WebP of a listing photo, or null for a photo it does
 *  not fetch. The query keeps one order: it is the edge cache key. */
export function resizedPhoto(url: string | null | undefined, w: (typeof CARD_PHOTO_WIDTHS)[number]): string | null {
  if (!url || !RESIZABLE.test(url)) return null;
  const src = encodeURIComponent(resizeSource(url));
  return `${mediaOrigin()}/api/media/photo.webp?w=${w}&v=${CARD_PHOTO_VERSION}&src=${src}`;
}

export interface CardPhotoSource {
  /** The <img> src: a resized copy, or the site's own card URL. */
  src: string;
  /** Every resized width, for the browser to pick from with `sizes`. */
  srcSet?: string;
  sizes?: string;
  /** Tried when the resized copy fails: the site's own card URL. */
  fallbackSrc?: string;
  /** A site the API does not fetch: its small phone copy, if it has one
   *  (a `(max-width: 640px)` <source>, as before). */
  phoneSrc?: string;
}

/** How a listing card shows its photo: the API's resized copies with a
 *  srcset for the card's `sizes` and the site's card URL as fallback;
 *  for a site the API does not fetch (yeniemlak.az, emlak.az, anything
 *  unknown), today's URLs. Null without a photo. */
export function cardPhoto(url: string | null | undefined, sizes: string): CardPhotoSource | null {
  const card = upgradeCardImage(url);
  if (!card) return null;
  if (!resizedPhoto(url, 480)) {
    return { src: card, phoneSrc: phoneCardImage(url) ?? undefined };
  }
  return {
    src: resizedPhoto(url, 480)!,
    srcSet: CARD_PHOTO_WIDTHS.map((w) => `${resizedPhoto(url, w)} ${w}w`).join(", "),
    sizes,
    fallbackSrc: card,
  };
}

/** The origin a card's photo comes from first (the API for a resized
 *  copy, else the site): what a page preconnects for its first cards. */
export function cardPhotoOrigin(url: string | null | undefined): string | null {
  const photo = cardPhoto(url, CARD_SIZES.grid);
  if (!photo) return null;
  const first = photo.phoneSrc ?? photo.src;
  try {
    return new URL(first).origin;
  } catch {
    return null;
  }
}
